/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }            from "@/util/logger";
import { Scenario }          from "@/models/scenario/scenario";

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  LineBasicMaterial,
  LineSegments,
  Plane,
  WebGLRenderer
} from 'three';

import { DecisionPoint } from "@/models/scenario/decision.point";



/**
 *
 * @class CrossingsBuilder
 *
 * this builder can make a single wireframe object for all diamond crossings
 * segments; basically when a crossing track is occupied at a decision point.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class CrossingsBuilder {

  /** timescale gets clipped so we don't go beyond simulation time */
  private  clippingPlane: Plane|null           = null;

  /** the geometric scale of the graph (vertical) */
  private    distanceScale: number             = 1.0;

  /** the geometric scale of the graph (horizontal) */
  private        timeScale: number             = 1.0;

  /** where the X data points start */
  private            dataX: number             = 0;

  /** where the Y data points start */
  private            dataY: number             = 0;

  /** the unscaled max time value */
  private        dataWidth: number             = 0;

  /** the unscaled max distance value */
  private       dataHeight: number             = 0;

  /** the (maybe timezone specific) scenario */
  private         scenario: Scenario|null       = null;

  /** the rendering pipeline */
  private         renderer: WebGLRenderer|null  = null;

  /** standard constructor */
  constructor(init?: Partial<CrossingsBuilder>) {

    Object.assign(this, init);
  }

  /** fetch the data start (horizontal) */
  public getX(): number {

    return this.dataX;
  }

  /** fetch the data start (vertical) */
  public getY(): number {

    return this.dataY;
  }

  /** fetch the data end (horizontal) */
  public getWidth(): number {

    return this.dataWidth;
  }

  /** fetch the data end (vertical) */
  public getHeight(): number {

    return this.dataHeight;
  }

  /** force the scenario */
  public setScenario(scenario: Scenario): CrossingsBuilder {

    this.scenario = scenario;

    return this;
  }

  /** fetch the scenario */
  public getScenario(): Scenario {

    return this.scenario;
  }

  /** force the distance (vertical) geometric scaling */
  public setDistanceScale(scale: number): CrossingsBuilder {

    this.distanceScale = scale;

    return this;
  }

  /** fetch the distance (vertical) geometric scaling */
  public getDistanceScale(): number {

    return this.distanceScale;
  }

  /** force the time (horizontal) geometric scaling */
  public setTimeScale(scale: number): CrossingsBuilder {

    this.timeScale = scale;

    return this;
  }

  /** fetch the time (horizontal) geometric scaling */
  public getTimeScale(): number {

    return this.timeScale;
  }

  /** force the clipping extent */
  public setClippingPlane(clip: Plane): CrossingsBuilder {

    this.clippingPlane = clip;

    return this;
  }

  /** fetch the clipping extent */
  public getClippingPlane(): Plane {

    return this.clippingPlane;
  }

  /** force the rendering pipeline */
  public setRenderer(pipeline: WebGLRenderer): CrossingsBuilder {

    this.renderer = pipeline;

    return this;
  }

  /** fetch the rendering pipeline */
  public getRenderer(): WebGLRenderer {

    return this.renderer;
  }


  /** build the set of line segments as a complete geometry/mesh */
  public build(): LineSegments|null {

    let b: CrossingsBuilder = this;

    if (!b.scenario || !b.clippingPlane || !b.renderer) {

      Logger.error(`[CrossingsBuilder] build - no scenario, renderer or clipping plane. Aborting.`);

      return null;
    }

    let crossings: Map<DecisionPoint, {segments: [number,number][]}> = b.scenario.crossings();

    if (crossings.size == 0) {

      Logger.debug(`[CrossingsBuilder] build - no crossing data.`);

      return null;
    }

    /* pre-scan for maximum number of vertices */

    let maxPoints: number = 0;
    let    nSegs: number  = 0;

    {
      for (let [, v] of crossings) {

        /* each segment of each decision point will need a start and end point */

        maxPoints += v.segments.length * 2;
        nSegs     += v.segments.length;

      }
    }

    /* get ready for line segment generation ... */

    let    geometry: BufferGeometry  = new BufferGeometry();
    let   positions: Float32Array    = new Float32Array(maxPoints * 3);
    let      colors: Float32Array    = new Float32Array(maxPoints * 3);
    let     indices: number[]        = [];
    let      vIndex: number          = 0;
    let      cIndex: number          = 0;
    let      pIndex: number          = 0;

    geometry.name = 'crossings';

    /* create the plot ... */

    /* walk through all the crossings */

    let     maxX: number             = 0;
    let     maxY: number             = 0;
    let     minX: number             = Number.POSITIVE_INFINITY;
    let     minY: number             = Number.POSITIVE_INFINITY;

    let   colorConverter             = new Color('#FFFF00');

    for (let [k, v] of crossings) {

      /* walk through the segments for this decision point... */

      for (let seg of v.segments) {

        let x1: number = seg[0];
        let y1: number = k._mileage;
        let x2: number = seg[1];
        let y2: number = k._mileage;

        if (x2 < x1) {

          /* Shark still looks fake. */

          const tmp: number = x1;
          x1 = x2;
          x2 = tmp;

        }

        /* draw the segment */

        let sx: number = x1 / 3600.0;
        let sy: number = y1;
        let ex: number = x2 / 3600.0;
        let ey: number = y2;

        if (isNaN(sx)) {
          sx = 0;
        }
        if (isNaN(sy)) {
          sy = 0;
        }
        if (isNaN(ex)) {
          ex = 0;
        }
        if (isNaN(ey)) {
          ey = 0;
        }

        /* set the outer corner of the graph bounding box */

        if (sx > maxX) {
          maxX = sx;
        }

        if (ex > maxX) {
          maxX = ex;
        }

        if (sy > maxY) {
          maxY = sy;
        }

        if (ey > maxY) {
          maxY = ey;
        }

        /* set the inner corner of the graph bounding box */

        if (sx < minX) {
          minX = sx;
        }

        if (ex < minX) {
          minX = ex;
        }

        if (sy < minY) {
           minY = sy;
        }

        if (ey < minY) {
          minY = ey;
        }

         /* push this segment */

        positions[vIndex]     = sx;
        positions[vIndex + 1] = sy;
        positions[vIndex + 2] = 0;

        positions[vIndex + 3] = ex;
        positions[vIndex + 4] = ey;
        positions[vIndex + 5] = 0;

        indices.push(pIndex);
        indices.push(pIndex + 1);

        vIndex += 6;
        pIndex += 2;

        /* push the color for this segment */

        colors[cIndex]     = colorConverter.r;
        colors[cIndex + 1] = colorConverter.g;
        colors[cIndex + 2] = colorConverter.b;

        colors[cIndex + 3] = colorConverter.r;
        colors[cIndex + 4] = colorConverter.g;
        colors[cIndex + 5] = colorConverter.b;

        cIndex += 6;

      } /* end of segment walk within a move */

    } /* end of decision point walk */

    /* set the content bound */

    b.dataWidth  = maxX;
    b.dataHeight = maxY;
    b.dataX      = minX;
    b.dataY      = minY;

    maxX *= b.timeScale;
    maxY *= b.distanceScale;

    Logger.debug('[CrossingsBuilder] build - world size: max x: ', maxX, ' max y: ', maxY);
    Logger.debug('[CrossingsBuilder] build - screen size: max x: ', b.renderer.domElement.getBoundingClientRect().width, ' max y: ', b.renderer.domElement.getBoundingClientRect().height);
    Logger.debug('[CrossingsBuilder] build - world size: min x: ', minX, ' min y: ', minY);
    Logger.debug('[CrossingsBuilder] build - rendering segments: ', nSegs);

    b.clippingPlane.constant = b.dataWidth * b.timeScale;

    /* create the buffered geometry for "one big line" that represents all the train movement lines */

    let material: LineBasicMaterial = new LineBasicMaterial( {
      color: 0xffffff,
      vertexColors: true,
      clippingPlanes: [b.clippingPlane]
    });

    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

    geometry.setAttribute( 'color', new BufferAttribute( colors, 3 ));

    /* all done! */

    return new LineSegments(geometry, material);
  }
}
